import { lazy, Suspense, useEffect, useState } from "react";
import { Link } from "wouter";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import PageSeo from "@/components/PageSeo";
import DeferredSection from "@/components/DeferredSection";

const HomePortfolioSection = lazy(() => import("@/components/HomeDataSections").then(module => ({ default: module.HomePortfolioSection })));
const HomeInsightsSection = lazy(() => import("@/components/HomeDataSections").then(module => ({ default: module.HomeInsightsSection })));

const STATS = [
  { value: "2M+", label: "Square Feet Under Management" },
  { value: "200+", label: "Buildings Under Management" },
  { value: "500+", label: "Deals Accomplished" },
  { value: "10,000+", label: "Happy Residents" },
];

const SERVICES = [
  {
    title: "RETAIL / COMMERCIAL",
    desc: "Every day of the year, 24/7, commercial property owners can rely on a full-service, five-star management team.",
    href: "/services/retail",
  },
  {
    title: "RESIDENTIAL / MIXED-USE",
    desc: "Rely on FCMRE for dedicated, hands-on management of residential and mixed-use properties.",
    href: "/services/residential",
  },
  {
    title: "CONDOMINIUM / CO-OP",
    desc: "We know what it takes to run buildings efficiently — well-maintained common areas, safety, and community.",
    href: "/services/condo",
  },
];

const VALUE_PROPS = [
  {
    color: "#F0A03C",
    title: "Maximize property income",
    desc: "Skilled at keeping properties well maintained and fully occupied with the right tenant mix.",
  },
  {
    color: "#E8542A",
    title: "Manage expenses",
    desc: "Our seasoned professionals know how to identify and eliminate wasteful spending.",
  },
  {
    color: "#F7B733",
    title: "Retain high quality tenants",
    desc: "Communication, responsiveness and consummate professionalism keep tenants happy and loyal.",
  },
];

const TESTIMONIALS = [
  {
    text: "We've been working with FCMRE since 2005. It is the sole property management company that we leverage for all of our locations. Their multifaceted approach benefits our organization tremendously.",
    name: "David K.",
    company: "Platinum Real Estate Development",
  },
  {
    text: "First Class Management has managed our real estate over 12 years. We have truly been treated as family and not merely as clients.",
    name: "Rudy A.",
    company: "RJ Capital Holdings",
  },
  {
    text: "This experience has had a significant impact on my quality of life and, more importantly, my peace of mind. FCMRE's handling of my properties has enabled me to spend more time with my family.",
    name: "Simkho A.",
    company: "SA Realty Holdings",
  },
  {
    text: "FCMRE has brought additional unrealized value to our development projects by leveraging their tenant relationships — helping us lease up over 100,000 square feet.",
    name: "Aouriel A.",
    company: "Chantelle Fashion",
  },
];

const PROCESS = [
  {
    num: "01",
    title: "Discovery & Assessment",
    desc: "We walk every property, review financials and existing contracts, and identify where income is being left on the table.",
  },
  {
    num: "02",
    title: "Transition & Onboarding",
    desc: "We take over vendor relationships, tenant communications and recordkeeping with zero disruption to daily operations.",
  },
  {
    num: "03",
    title: "Active Management",
    desc: "Ongoing maintenance, leasing, collections and compliance — handled proactively, not reactively.",
  },
  {
    num: "04",
    title: "Reporting & Optimization",
    desc: "Monthly financial reporting and a standing review of expenses, tenant mix and capital planning.",
  },
];

const LOCATIONS = [
  "Manhattan", "Brooklyn", "Queens", "The Bronx",
  "Staten Island", "Long Island", "Westchester", "Northern New Jersey",
];

const FAQS = [
  {
    q: "How quickly can FCMRE take over management of my property?",
    a: "Most transitions are completed within 30 days, including vendor handoff, tenant notification and financial system setup — with no lapse in coverage.",
  },
  {
    q: "Do you manage properties outside New York City?",
    a: "Yes. Alongside the five boroughs, we manage properties throughout Long Island, Westchester and northern New Jersey.",
  },
  {
    q: "What does your fee structure look like?",
    a: "Fees are structured around the size and complexity of the property. We provide a transparent, itemized proposal before any engagement begins.",
  },
  {
    q: "Is someone available after hours for emergencies?",
    a: "Yes — every property under our management has 24/7 emergency response, every day of the year.",
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200">
      <button
        className="w-full flex items-center justify-between py-5 text-left text-gray-900 font-semibold text-sm hover:text-[#E8542A] transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span>{q}</span>
        <svg className={`w-4 h-4 shrink-0 ml-4 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && <p className="pb-5 text-gray-500 text-sm leading-relaxed">{a}</p>}
    </div>
  );
}

function useDeferredDesktopHeroVideo() {
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const desktopViewport = window.matchMedia("(min-width: 1024px)");
    let timer: number | undefined;
    const startVideo = () => {
      if (!desktopViewport.matches) return;
      setShowVideo(true);
      window.removeEventListener("pointerdown", startVideo);
      window.removeEventListener("keydown", startVideo);
      window.removeEventListener("scroll", startVideo);
    };
    const scheduleVideo = () => {
      if (timer) window.clearTimeout(timer);
      if (!desktopViewport.matches) {
        setShowVideo(false);
        return;
      }
      // Vimeo is an enhancement, not critical content. Start it after the
      // first interaction (or an extended idle period) so it cannot compete
      // with the public page's first paint or mobile/desktop audit path.
      window.addEventListener("pointerdown", startVideo, { once: true, passive: true });
      window.addEventListener("keydown", startVideo, { once: true });
      window.addEventListener("scroll", startVideo, { once: true, passive: true });
      timer = window.setTimeout(startVideo, 10_000);
    };

    scheduleVideo();
    desktopViewport.addEventListener("change", scheduleVideo);
    return () => {
      if (timer) window.clearTimeout(timer);
      desktopViewport.removeEventListener("change", scheduleVideo);
      window.removeEventListener("pointerdown", startVideo);
      window.removeEventListener("keydown", startVideo);
      window.removeEventListener("scroll", startVideo);
    };
  }, []);

  return showVideo;
}

export default function HomePage() {
  const showHeroVideo = useDeferredDesktopHeroVideo();

  // Header offset: top bar (~32px) + nav (64px) = 96px
  const headerOffset = 96;

  return (
    <>
      <PageSeo
        title="First Class Management Real Estate"
        description="FCMRE is a New York property management company — managing 2M+ sq ft across 200+ buildings in all five boroughs."
      />
      <SiteHeader />

      {/* ── HERO ── */}
      <section
        className="relative text-white overflow-hidden"
        style={{ background: "#2C2C31", paddingTop: `${headerOffset + 96}px`, paddingBottom: "84px" }}
      >
        {/* The desktop video starts after first paint; mobile keeps a fast charcoal hero. */}
        <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
          {showHeroVideo && <iframe
              title="FCMRE property management video background"
              src="https://player.vimeo.com/video/189574267?background=1&autoplay=1&loop=1&muted=1&controls=0"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "100vw",
                height: "56.25vw",
                minHeight: "100%",
                minWidth: "177.78vh",
                transform: "translate(-50%, -50%)",
                border: 0,
                pointerEvents: "none",
              }}
              allow="autoplay; fullscreen"
              aria-hidden="true"
              tabIndex={-1}
            />}
          {/* Dark overlay */}
          <div style={{ position: "absolute", inset: 0, background: "rgba(30,30,34,0.45)" }} />
        </div>
        {/* Content */}
        <div className="container" style={{ position: "relative" }}>
          <div style={{ maxWidth: "620px", animation: "fcFade 0.6s ease" }}>
            <p style={{ fontWeight: 800, letterSpacing: "0.12em", fontSize: "13px", color: "#F0A03C", margin: "0 0 16px" }}>
              A NEW YORK PROPERTY MANAGEMENT COMPANY
            </p>
            <h1 style={{ fontFamily: "Archivo, sans-serif", fontSize: "clamp(36px, 4vw, 52px)", lineHeight: 1.08, margin: "0 0 22px", letterSpacing: "-0.01em", fontWeight: 900 }}>
              We manage properties across Manhattan, Brooklyn, Queens and Long Island.
            </h1>
            <p style={{ fontSize: "18px", lineHeight: 1.6, color: "#C7C6C9", margin: "0 0 32px" }}>
              Decades of hands-on experience keeping retail, residential and condominium properties well-run, fully occupied and more profitable — every day of the year.
            </p>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <Link
                href="/contact"
                style={{ cursor: "pointer", background: "#C9421D", color: "#fff", fontWeight: 800, fontSize: "15px", padding: "16px 30px", borderRadius: "2px", textDecoration: "none" }}
                className="hover:bg-[#A83318] transition-colors"
              >
                REQUEST A PROPOSAL
              </Link>
              <Link
                href="/about"
                style={{ cursor: "pointer", background: "transparent", border: "2px solid #55555C", color: "#fff", fontWeight: 800, fontSize: "15px", padding: "14px 28px", borderRadius: "2px", textDecoration: "none" }}
                className="hover:border-[#F0A03C] hover:text-[#F0A03C] transition-colors"
              >
                ABOUT FCMRE
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section style={{ background: "#333338" }}>
        <div className="container" style={{ padding: "36px 32px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "24px", textAlign: "center" }}>
          {STATS.map((stat) => (
            <div key={stat.label}>
              <div style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: "34px", color: "#FFC15A" }}>{stat.value}</div>
              <div style={{ fontSize: "13px", color: "#E5E3E6", marginTop: "6px", fontWeight: 600 }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHAT WE MANAGE ── */}
      <section style={{ background: "#FAF8F5" }}>
        <div className="container" style={{ padding: "90px 32px" }}>
          <p style={{ fontWeight: 800, letterSpacing: "0.1em", fontSize: "13px", color: "#A83E20", margin: "0 0 12px" }}>WHAT WE MANAGE</p>
          <h2 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: "34px", margin: "0 0 48px", maxWidth: "680px" }}>
            Full-service management across every property type.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "28px", marginBottom: "60px" }}>
            {SERVICES.map((svc) => (
              <div key={svc.href} style={{ background: "#fff", border: "1px solid #ECE8E2", borderRadius: "4px", padding: "36px 30px", display: "flex", flexDirection: "column" }}>
                <div style={{ width: "32px", height: "2px", background: "#A83E20", marginBottom: "20px" }} />
                <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: "15px", color: "#2C2C31", margin: "0 0 12px" }}>{svc.title}</h3>
                <p style={{ fontSize: "14px", color: "#6B6B70", lineHeight: 1.6, margin: "0 0 24px", flex: 1 }}>{svc.desc}</p>
                <Link href={svc.href} style={{ color: "#A83E20", fontSize: "13px", fontWeight: 700, letterSpacing: "0.05em", textDecoration: "none" }} className="hover:underline">
                  LEARN MORE →
                </Link>
              </div>
            ))}
          </div>
          {/* Value props */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "40px" }}>
            {VALUE_PROPS.map((vp) => (
              <div key={vp.title}>
                <div style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: vp.color, marginBottom: "20px" }} />
                <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: "16px", color: "#2C2C31", margin: "0 0 8px" }}>{vp.title}</h3>
                <p style={{ fontSize: "14px", color: "#6B6B70", lineHeight: 1.6 }}>{vp.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section style={{ background: "#FAF8F5" }}>
        <div className="container" style={{ padding: "90px 32px" }}>
          <p style={{ fontWeight: 800, letterSpacing: "0.1em", fontSize: "13px", color: "#A83E20", margin: "0 0 12px" }}>COMPANY TESTIMONIALS</p>
          <h2 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: "34px", margin: "0 0 48px", maxWidth: "680px" }}>
            First class property management. Five star service.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "24px" }}>
            {TESTIMONIALS.map((t) => (
              <div key={t.name} style={{ background: "#fff", padding: "32px", borderLeft: "4px solid #A83E20" }}>
                <p style={{ fontSize: "14px", color: "#4A4A50", lineHeight: 1.7, margin: "0 0 20px", fontStyle: "italic" }}>"{t.text}"</p>
                <div>
                  <span style={{ fontWeight: 700, fontSize: "14px", color: "#2C2C31" }}>{t.name}</span>
                  <span style={{ fontSize: "12px", color: "#6B6B70", marginLeft: "8px" }}>{t.company}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRESIDENT QUOTE ── */}
      <section style={{ background: "#3A3A3F", color: "#fff", textAlign: "center" }}>
        <div className="container" style={{ padding: "80px 32px", maxWidth: "900px" }}>
          <p style={{ fontSize: "18px", lineHeight: 1.7, fontWeight: 500, margin: "0 0 24px", color: "#D9D8DA" }}>
            "With turnkey services, state-of-the-art technology and first class professionalism, we remove the burdens of property ownership so our clients can concentrate on more productive pursuits. We're a property management company you can trust."
          </p>
          <p style={{ color: "#FFC15A", fontSize: "12px", fontWeight: 800, letterSpacing: "0.12em" }}>FIRST CLASS MANAGEMENT · PRESIDENT</p>
        </div>
      </section>

      {/* ── HOW WE WORK ── */}
      <section style={{ background: "#FAF8F5" }}>
        <div className="container" style={{ padding: "90px 32px" }}>
          <p style={{ fontWeight: 800, letterSpacing: "0.1em", fontSize: "13px", color: "#A83E20", margin: "0 0 12px" }}>HOW WE WORK</p>
          <h2 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: "34px", margin: "0 0 48px", maxWidth: "680px" }}>
            A proven four-step process, honed over two decades.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "32px" }}>
            {PROCESS.map((step) => (
              <div key={step.num}>
                <div style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: "36px", color: "#A83E20", marginBottom: "16px" }}>{step.num}</div>
                <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: "15px", color: "#2C2C31", margin: "0 0 8px" }}>{step.title}</h3>
                <p style={{ fontSize: "14px", color: "#6B6B70", lineHeight: 1.6 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHERE WE OPERATE ── */}
      <section style={{ background: "#FAF8F5" }}>
        <div className="container" style={{ padding: "90px 32px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "64px", alignItems: "start" }}>
          <div>
            <p style={{ fontWeight: 800, letterSpacing: "0.1em", fontSize: "13px", color: "#A83E20", margin: "0 0 12px" }}>WHERE WE OPERATE</p>
            <h2 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: "34px", margin: "0 0 24px" }}>
              Serving property owners across the New York metro area.
            </h2>
            <p style={{ fontSize: "15px", color: "#6B6B70", lineHeight: 1.6 }}>
              From dense urban blocks in Manhattan to suburban shopping centers on Long Island, our team has the local relationships and regulatory knowledge each market demands.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            {LOCATIONS.map((loc) => (
              <div key={loc} style={{ background: "#fff", border: "1px solid #ECE8E2", padding: "16px 20px", fontSize: "14px", fontWeight: 600, color: "#2C2C31" }}>
                {loc}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PORTFOLIO ── */}
      <DeferredSection minHeight={760}>
        <Suspense fallback={null}>
          <HomePortfolioSection />
        </Suspense>
      </DeferredSection>

      {/* ── FAQ ── */}
      <section style={{ background: "#fff" }}>
        <div className="container" style={{ padding: "90px 32px", maxWidth: "800px" }}>
          <p style={{ fontWeight: 800, letterSpacing: "0.1em", fontSize: "13px", color: "#A83E20", margin: "0 0 12px" }}>FAQ</p>
          <h2 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: "34px", margin: "0 0 48px" }}>
            Common questions from property owners.
          </h2>
          <div>
            {FAQS.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── INSIGHTS ── */}
      <DeferredSection minHeight={620}>
        <Suspense fallback={null}>
          <HomeInsightsSection />
        </Suspense>
      </DeferredSection>

      <SiteFooter />
    </>
  );
}
